import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #isSizeVisible = false;

  /** @type {boolean} */
  #sizeSelectedByUser = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    target?.addEventListener(
      'change',
      this.#handleVariantOptionChange,
      { signal }
    );

    this.#getInitialQuantity();

    /**
     * 页面第一次加载时同步移动端按钮状态。
     */
    this.#updateMobileBuyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * 监听产品页原购买区域。
   *
   * 当原来的 Add to Cart 区域滚出屏幕后，
   * 才显示底部 Sticky Bar。
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) return;

    const buyButtonsBlock =
      productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) return;

    this.#targetAddToCartButton =
      productForm.querySelector(
        '[ref="addToCartButton"]'
      );

    /**
     * 移动端固定栏始终存在，只根据 Size 是否在屏幕内
     * 切换 SELECT SIZE 或 ADD TO CART + BUY NOW。
     */
    if (
      window.matchMedia(
        '(max-width: 749px)'
      ).matches
    ) {
      const sizeTarget =
        this.#getSizeTarget();

      if (!sizeTarget) return;

      this.#buyButtonsIntersectionObserver =
        new IntersectionObserver(
          (entries) => {
            const [entry] = entries;

            if (!entry) return;

            this.#isSizeVisible =
              entry.isIntersecting;

            this.#updateMobileBuyState();
          },
          {
            threshold: 0.08,
          }
        );

      this.#buyButtonsIntersectionObserver.observe(
        sizeTarget
      );

      return;
    }

    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (!footer) return;

    /**
     * 监听主购买按钮。
     */
    this.#buyButtonsIntersectionObserver =
      new IntersectionObserver((entries) => {
        const [entry] = entries;

        if (!entry) return;

        /**
         * 原购买区域已经滑到屏幕上方。
         */
        if (!entry.isIntersecting && !this.#isStuck) {
          const rect =
            entry.target.getBoundingClientRect();

          if (
            rect.bottom < 0 ||
            rect.top < 0
          ) {
            this.#showStickyBar();
          }
        }

        /**
         * 用户重新滚动回原购买区域。
         */
        else if (
          entry.isIntersecting &&
          this.#isStuck
        ) {
          this.#hiddenByBottom = false;

          this.#hideStickyBar();
        }
      });

    /**
     * 到 Footer 时隐藏 Sticky。
     */
    this.#mainBottomObserver =
      new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          if (!entry) return;

          if (
            entry.isIntersecting &&
            this.#isStuck
          ) {
            this.#hiddenByBottom = true;

            this.#hideStickyBar();
          }

          else if (
            !entry.isIntersecting &&
            this.#hiddenByBottom
          ) {
            const rect =
              buyButtonsBlock.getBoundingClientRect();

            if (
              rect.bottom < 0 ||
              rect.top < 0
            ) {
              this.#hiddenByBottom = false;

              this.#showStickyBar();
            }
          }
        },
        {
          rootMargin:
            '200px 0px 0px 0px',
        }
      );

    this.#buyButtonsIntersectionObserver.observe(
      buyButtonsBlock
    );

    this.#mainBottomObserver.observe(
      footer
    );

    /**
     * 获取产品页真正的 Add To Cart 按钮。
     *
     * Sticky Add To Cart 不会自己重新加购物车，
     * 而是点击 Shopify 原来的按钮。
     */
    this.#targetAddToCartButton =
      productForm.querySelector(
        '[ref="addToCartButton"]'
      );
  }

  /**
   * Sticky ADD TO CART
   */
  handleAddToCartClick = async () => {
    const isMobile = window.matchMedia(
      '(max-width: 749px)'
    ).matches;

    /**
     * Size 不在手机屏幕中时，该按钮只负责返回尺码区域。
     */
    if (
      isMobile &&
      !this.#isSizeVisible
    ) {
      const sizeTarget =
        this.#getSizeTarget();

      if (sizeTarget) {
        sizeTarget.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      return;
    }

    if (!this.#targetAddToCartButton) {
      return;
    }

    /**
     * 调用 Horizon 原来的 Add To Cart。
     */
    this.#targetAddToCartButton.dataset.puppet =
      'true';

    this.#targetAddToCartButton.click();

    const cartIcon =
      this.#getVisibleCartDestination();

    const mobileAddButton =
      this.querySelector(
        '.konhill-mobile-sticky__add'
      );

    const animationButton =
      isMobile &&
      mobileAddButton instanceof
        HTMLButtonElement
        ? mobileAddButton
        : this.refs.addToCartButton;

    const animationSource =
      isMobile
        ? animationButton
        : this.refs.productImage;

    if (
      !cartIcon ||
      !animationButton ||
      !animationSource ||
      !this.refs.productImage
    ) {
      return;
    }

    if (
      animationButton.dataset.added !==
      'true'
    ) {
      animationButton.dataset.added =
        'true';
    }

    if (this.#resetTimeout) {
      clearTimeout(
        this.#resetTimeout
      );
    }

    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement(
          'fly-to-cart'
        )
      );

    flyToCartElement.classList.add(
      'fly-to-cart--sticky'
    );

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );

    flyToCartElement.useSourceSize =
      isMobile ? false : 'true';

    flyToCartElement.source =
      animationSource;

    flyToCartElement.destination =
      cartIcon;

    document.body.appendChild(
      flyToCartElement
    );

    await onAnimationEnd([
      animationButton,
      flyToCartElement,
    ]);

    this.#resetTimeout =
      setTimeout(() => {
        animationButton.removeAttribute(
          'data-added'
        );
      }, 800);
  };

  /**
   * BUY NOW
   *
   * 使用当前选中的 Variant
   * 直接进入 Shopify Checkout。
   */
  handleBuyNowClick = () => {
    const variantId =
      this.dataset.currentVariantId;

    const isAvailable =
      this.dataset.variantAvailable ===
      'true';

    if (
      !variantId ||
      !isAvailable
    ) {
      return;
    }

    const quantity =
      Math.max(
        1,
        this.#currentQuantity || 1
      );

    const root =
      window.Shopify?.routes?.root ||
      '/';

    const normalizedRoot =
      root.endsWith('/')
        ? root
        : `${root}/`;

    window.location.href =
      `${normalizedRoot}cart/${encodeURIComponent(
        variantId
      )}:${quantity}?checkout`;
  };

  /**
   * Shopify Variant 更新。
   */
  #handleVariantUpdate = (
    event
  ) => {
    if (
      event.detail.data.productId !==
      this.dataset.productId
    ) {
      return;
    }

    const variant =
      event.detail.resource;

    /**
     * Shopify 返回新的产品 HTML。
     */
    const newStickyAddToCart =
      event.detail.data.html.querySelector(
        'sticky-add-to-cart'  
      );

    if (!newStickyAddToCart) {
      return;
    }

    const newStickyBar =
      newStickyAddToCart.querySelector(
        '[ref="stickyBar"]'
      );

    if (!newStickyBar) {
      return;
    }

    /**
     * 保存当前 Sticky 是否显示。
     */
    const currentStuck =
      this.refs.stickyBar.getAttribute(
        'data-stuck'
      ) || 'false';

    const variantAvailable =
      newStickyAddToCart.dataset.variantAvailable;

    /**
     * 使用 Horizon 原来的 morph 更新内容。
     */
    morph(
      this.refs.stickyBar,
      newStickyBar,
      {
        childrenOnly: true,
      }
    );

    /**
     * 恢复 Sticky 显示状态。
     */
    this.refs.stickyBar.setAttribute(
      'data-stuck',
      currentStuck
    );

    this.dataset.variantAvailable =
      variantAvailable;

    /**
     * 保存当前 Variant ID。
     */
    if (
      variant &&
      variant.id
    ) {
      this.dataset.currentVariantId =
        variant.id;
    }

    /**
     * Variant 更新后，
     * 重新获取真正的 Add To Cart 按钮。
     */
    const productForm =
      this.#getProductForm();

    if (productForm) {
      this.#targetAddToCartButton =
        productForm.querySelector(
          '[ref="addToCartButton"]'
        );
    }

    /**
     * Variant 不存在。
     */
    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    /**
     * 更新数量显示。
     */
    this.#updateButtonText();

    /**
     * morph 会重新生成 Sticky HTML，
     * 因此重新应用移动端按钮状态。
     */
    this.#updateMobileBuyState();
  };

  /**
   * Variant Selected。
   */
  #handleVariantSelected = (
    event
  ) => {
    const variantId =
      event.detail.resource?.id;

    if (!variantId) {
      return;
    }

    this.dataset.currentVariantId =
      variantId;
  };

  /**
   * 只有客户实际操作 Size 时才显示移动端价格。
   */
  #handleVariantOptionChange = (
    event
  ) => {
    const control = event.target;

    if (
      !(
        control instanceof
          HTMLInputElement ||
        control instanceof
          HTMLSelectElement
      )
    ) {
      return;
    }

    const optionName =
      control.getAttribute(
        'data-option-name'
      ) || '';

    if (!this.#isSizeOptionName(optionName)) {
      return;
    }

    this.#sizeSelectedByUser = true;
    this.#updateMobileBuyState();
  };

  /**
   * 当前组合不存在。
   */
  #handleVariantUnavailable =
    () => {
      this.dataset.currentVariantId =
        '';

      const variantTitleElement =
        this.querySelector(
          '.sticky-add-to-cart__variant'
        );

      const productId =
        this.dataset.productId;

      const variantPicker =
        document.querySelector(
          `variant-picker[data-product-id="${productId}"]`
        );

      if (
        !variantTitleElement ||
        !variantPicker
      ) {
        return;
      }

      const selectedOptions =
        Array.from(
          variantPicker.querySelectorAll(
            'input:checked'
          )
        )
          .map(
            (option) =>
              /** @type {HTMLInputElement} */ (
                option
              ).value
          )
          .filter(
            (value) =>
              value !== ''
          )
          .join(' / ');

      if (!selectedOptions) {
        return;
      }

      variantTitleElement.textContent =
        selectedOptions;
    };

  /**
   * Add To Cart 完成。
   */
  #handleCartAddComplete = (
    _event
  ) => {
    if (
      this.#targetAddToCartButton
    ) {
      this.#targetAddToCartButton.dataset.puppet =
        'false';
    }
  };

  /**
   * Quantity 更新。
   */
  #handleQuantityUpdate = (
    event
  ) => {
    /**
     * 不处理 Cart Drawer
     * 里面的数量变化。
     */
    if (event.detail.cartLine) {
      return;
    }

    this.#currentQuantity =
      event.detail.quantity;

    this.#updateButtonText();
  };

  /**
   * 显示 Sticky Bar。
   */
  #showStickyBar() {
    const { stickyBar } =
      this.refs;

    this.#isStuck = true;

    stickyBar.dataset.stuck =
      'true';
  }

  /**
   * 隐藏 Sticky Bar。
   */
  #hideStickyBar() {
    const { stickyBar } =
      this.refs;

    this.#isStuck = false;

    stickyBar.dataset.stuck =
      'false';
  }

  /**
   * 控制移动端按钮状态。
   */
  #updateMobileBuyState() {
    const buyActions =
      this.querySelector(
        '[ref="mobileBuyActions"]'
      );

    if (
      !(buyActions instanceof HTMLElement)
    ) {
      return;
    }

    buyActions.hidden = false;

    const available =
      this.dataset.variantAvailable ===
      'true';

    const addButton =
      this.querySelector(
        '.konhill-mobile-sticky__add'
      );

    const buyNowButton =
      this.querySelector(
        '.konhill-mobile-sticky__buy-now'
      );

    const addText =
      this.querySelector(
        '[ref="mobileAddToCartText"]'
      );

    const mobilePrice =
      this.querySelector(
        '[ref="mobilePrice"]'
      );

    if (
      mobilePrice instanceof
      HTMLElement
    ) {
      mobilePrice.hidden =
        !this.#sizeSelectedByUser;
    }

    /**
     * Size 不在屏幕中：显示单个 SELECT SIZE。
     */
    if (!this.#isSizeVisible) {
      buyActions.dataset.mode =
        'select-size';

      if (
        addButton instanceof
        HTMLButtonElement
      ) {
        addButton.disabled = false;
        addButton.setAttribute(
          'aria-label',
          'Select size'
        );
      }

      if (
        buyNowButton instanceof
        HTMLButtonElement
      ) {
        buyNowButton.hidden = true;
      }

      if (
        addText instanceof
        HTMLElement
      ) {
        addText.textContent =
          'SELECT SIZE';
      }

      return;
    }

    /**
     * Size 在屏幕中：恢复 ADD TO CART + BUY NOW。
     */
    buyActions.dataset.mode = 'buy';

    if (
      buyNowButton instanceof
      HTMLButtonElement
    ) {
      buyNowButton.hidden = false;
    }

    /**
     * 售罄则禁用 Add To Cart。
     */
    if (
      addButton instanceof
      HTMLButtonElement
    ) {
      addButton.disabled =
        !available;
      addButton.setAttribute(
        'aria-label',
        available
          ? 'Add to cart'
          : 'Sold out'
      );
    }

    /**
     * 售罄则禁用 Buy Now。
     */
    if (
      buyNowButton instanceof
      HTMLButtonElement
    ) {
      buyNowButton.disabled =
        !available;
    }

    /**
     * 更新按钮文本。
     */
    if (
      addText instanceof
      HTMLElement
    ) {
      addText.textContent =
        available
          ? 'ADD TO CART'
          : 'SOLD OUT';
    }
  }

  /**
   * 获取产品表单。
   */
  #getProductForm() {
    const productId =
      this.dataset.productId;

    if (!productId) {
      return null;
    }

    const sectionElement =
      this.closest(
        '.shopify-section'
      );

    if (!sectionElement) {
      return null;
    }

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getVisibleCartDestination() {
    const candidates = Array.from(
      document.querySelectorAll(
        '.header-actions__cart-icon, [data-testid="cart-icon"], cart-icon, a[href$="/cart"], a[href*="/cart?"]'
      )
    ).filter((element) => {
      const rect =
        element.getBoundingClientRect();

      const style =
        window.getComputedStyle(element);

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity) !== 0
      );
    });

    candidates.sort((first, second) => {
      const firstRect =
        first.getBoundingClientRect();

      const secondRect =
        second.getBoundingClientRect();

      return (
        firstRect.top - secondRect.top ||
        secondRect.right - firstRect.right
      );
    });

    return candidates[0] || null;
  }

  /**
   * 判断一个产品选项名称是否表示尺码。
   */
  #isSizeOptionName(optionName) {
    const normalizedName =
      optionName.trim().toLowerCase();

    return (
      normalizedName.includes('size') ||
      normalizedName.includes('尺码') ||
      normalizedName.includes('größe') ||
      normalizedName.includes('taille') ||
      normalizedName.includes('talla') ||
      normalizedName.includes('taglia') ||
      normalizedName.includes('maat') ||
      normalizedName.includes('storlek') ||
      normalizedName.includes('tamanho')
    );
  }

  /**
   * 获取当前产品的 Size 选择区域。
   * 支持按钮和下拉框两种 Horizon Variant Picker。
   */
  #getSizeTarget() {
    const productId =
      this.dataset.productId;

    const sectionElement =
      this.closest(
        '.shopify-section'
      );

    if (
      !productId ||
      !sectionElement
    ) {
      return null;
    }

    const variantPicker =
      sectionElement.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );

    if (!(variantPicker instanceof HTMLElement)) {
      return null;
    }

    const sizeControl =
      Array.from(
        variantPicker.querySelectorAll(
          '[data-option-name]'
        )
      ).find((control) =>
        this.#isSizeOptionName(
          control.getAttribute(
            'data-option-name'
          ) || ''
        )
      );

    if (sizeControl) {
      return (
        sizeControl.closest(
          'fieldset, .variant-option--dropdowns, .variant-option'
        ) || variantPicker
      );
    }

    const optionGroups =
      variantPicker.querySelectorAll(
        'fieldset, .variant-option--dropdowns'
      );

    return (
      optionGroups[
        optionGroups.length - 1
      ] || variantPicker
    );
  }

  /**
   * 初始 Quantity。
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity ||
          '1'
      ) || 1;

    this.#updateButtonText();
  }

  /**
   * Horizon 原数量显示。
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    const available =
      !addToCartButton.disabled;

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    if (
      available &&
      this.#currentQuantity > 1
    ) {
      quantityDisplay.style.display =
        'inline';
    } else {
      quantityDisplay.style.display =
        'none';
    }
  }
}

if (
  !customElements.get(
    'sticky-add-to-cart'
  )
) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}
